import React from 'react';
import type { EvidenceSource } from '../../types/evidence';
import evidenceAPI from '../../services/evidenceAPI';
import { RefreshCw, CheckCircle, XCircle, AlertCircle, Clock, Edit2, Trash2 } from 'lucide-react';

interface EvidenceSourceCardProps {
  source: EvidenceSource;
  onSync?: (sourceId: number) => void;
  onEdit?: (source: EvidenceSource) => void;
  onDelete?: (sourceId: number) => void;
  isSyncing?: boolean;
}

const EvidenceSourceCard: React.FC<EvidenceSourceCardProps> = ({
  source,
  onSync,
  onEdit,
  onDelete,
  isSyncing = false
}) => {
  const getStatusIcon = () => {
    switch (source.status) {
      case 'active':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'syncing':
        return <RefreshCw className="h-5 w-5 text-blue-600 animate-spin" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'inactive':
        return <AlertCircle className="h-5 w-5 text-gray-600" />;
      default:
        return <Clock className="h-5 w-5 text-yellow-600" />;
    }
  };

  const handleSync = async () => {
    if (onSync && !isSyncing) {
      onSync(source.id);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{evidenceAPI.getSourceIcon(source.source_type)}</span>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{source.source_name}</h3>
            <p className="text-sm text-gray-500">{evidenceAPI.formatSourceName(source.source_type)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onEdit && (
            <button
              onClick={() => onEdit(source)}
              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
              title="Edit source"
            >
              <Edit2 className="h-4 w-4" />
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(source.id)}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
              title="Delete source"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          {getStatusIcon()}
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${evidenceAPI.getStatusColor(source.status)}`}>
            {source.status}
          </span>
        </div>
      </div>

      {/* Description */}
      {source.source_description && (
        <p className="text-sm text-gray-600 mb-4 line-clamp-2">
          {source.source_description}
        </p>
      )}

      {/* Last Sync Info */}
      <div className="mb-3 pb-3 border-b border-gray-200">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">
            {source.source_type === 'ssl_labs' ? 'Last Scan:' : 'Last Sync:'}
          </span>
          <span className="font-medium text-gray-900">{formatDate(source.last_sync_date)}</span>
        </div>
      </div>

      {/* Evidence Breakdown - GitHub specific */}
      {source.source_type === 'github' && (
        <div className="mb-4">
          <p className="text-xs font-semibold text-gray-700 mb-3">Repository Evidence</p>
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 border border-blue-200">
              <p className="text-xs text-blue-600 font-medium mb-1">Pull Requests</p>
              <p className="text-2xl font-bold text-blue-700">
                {(source as any).pr_count || 0}
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-3 border border-purple-200">
              <p className="text-xs text-purple-600 font-medium mb-1">Commits</p>
              <p className="text-2xl font-bold text-purple-700">
                {(source as any).commit_count || 0}
              </p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-3 border border-green-200">
              <p className="text-xs text-green-600 font-medium mb-1">Approvals</p>
              <p className="text-2xl font-bold text-green-700">
                {(source as any).approval_count || 0}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Evidence Breakdown - SSL Labs specific */}
      {source.source_type === 'ssl_labs' && (
        <div className="mb-4">
          <p className="text-xs font-semibold text-gray-700 mb-2">SSL/TLS Evidence</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-3 border border-green-200">
              <p className="text-xs text-green-600">Certificates</p>
              <p className="text-2xl font-bold text-green-700">
                {source.certificate_count || source.last_evidence_count || 0}
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-3 border border-blue-200">
              <p className="text-xs text-blue-600">SSL Grade</p>
              <p className={`text-2xl font-bold ${
                source.ssl_grade === 'A+' || source.ssl_grade === 'A' ? 'text-green-600' :
                source.ssl_grade === 'B' ? 'text-blue-600' :
                source.ssl_grade === 'C' ? 'text-yellow-600' :
                'text-red-600'
              }`}>
                {source.ssl_grade || 'N/A'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Evidence Breakdown - AWS specific */}
      {source.source_type === 'aws_cloud' && (
        <div className="mb-4">
          <p className="text-xs font-semibold text-gray-700 mb-2">Cloud Infrastructure</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg p-3 border border-orange-200">
              <p className="text-xs text-orange-600">Security Rules</p>
              <p className="text-2xl font-bold text-orange-700">
                {source.security_rules_count || 0}
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-lg p-3 border border-purple-200">
              <p className="text-xs text-purple-600">Resources</p>
              <p className="text-2xl font-bold text-purple-700">
                {source.resource_count || source.last_evidence_count || 0}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Evidence Breakdown - SIEM specific */}
      {(source.source_type === 'siem_elastic' || source.source_type === 'siem_splunk' || source.source_type === 'siem_sentinel') && (
        <div className="mb-4">
          <p className="text-xs font-semibold text-gray-700 mb-2">Security Monitoring</p>
          {(source.total_events || 0) === 0 ? (
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 text-center">
              {source.status === 'archived' ? (
                <p className="text-xs text-gray-500">No new logs: this source is archived</p>
              ) : (
                <>
                  <p className="text-xs text-gray-500">No logs yet — waiting for data from SIEM</p>
                  <p className="text-xs text-gray-400 mt-1">Sync again after logs start flowing</p>
                </>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-3 border border-blue-200">
                <p className="text-xs text-blue-600 font-medium">Total Events</p>
                <p className="text-2xl font-bold text-blue-700">{source.total_events || 0}</p>
              </div>
              <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-lg p-3 border border-red-200">
                <p className="text-xs text-red-600 font-medium">Errors / Warnings</p>
                <p className="text-2xl font-bold text-red-700">{(source as any).error_events || source.failed_logins || 0}</p>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-3 border border-green-200">
                <p className="text-xs text-green-600 font-medium">Info Events</p>
                <p className="text-2xl font-bold text-green-700">{(source as any).info_events || source.successful_logins || 0}</p>
              </div>
              <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-lg p-3 border border-amber-200">
                <p className="text-xs text-amber-600 font-medium">SIEM Alerts</p>
                <p className="text-2xl font-bold text-amber-700">{source.alert_count || 0}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Evidence Breakdown - Jarvis Audit Logs specific */}
      {source.source_type === 'jarvis_audit_logs' && (
        <div className="mb-4">
          <p className="text-xs font-semibold text-gray-700 mb-2">Platform Activity <span className="font-normal text-gray-400">(Last 7 days)</span></p>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-lg p-3 border border-indigo-200">
              <p className="text-xs text-indigo-600 font-medium mb-1">Successful Logins</p>
              <p className="text-2xl font-bold text-indigo-700">
                {(source as any).login_count || 0}
              </p>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-lg p-3 border border-red-200">
              <p className="text-xs text-red-600 font-medium mb-1">Failed Logins</p>
              <p className="text-2xl font-bold text-red-700">
                {(source as any).failed_login_count || 0}
              </p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-3 border border-green-200">
              <p className="text-xs text-green-600 font-medium mb-1">MFA Events</p>
              <p className="text-2xl font-bold text-green-700">
                {(source as any).mfa_event_count || 0}
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-lg p-3 border border-purple-200">
              <p className="text-xs text-purple-600 font-medium mb-1">Security Events</p>
              <p className="text-2xl font-bold text-purple-700">
                {(source as any).security_event_count || 0}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Evidence Breakdown - Other sources */}
      {source.source_type !== 'github' && source.source_type !== 'ssl_labs' && source.source_type !== 'aws_cloud' &&
       source.source_type !== 'siem_elastic' && source.source_type !== 'siem_splunk' && source.source_type !== 'siem_sentinel' &&
       source.source_type !== 'jarvis_audit_logs' && (
        <div className="mb-4">
          <p className="text-xs font-semibold text-gray-700 mb-2">Evidence Collected</p>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-3xl font-bold text-gray-900">{source.last_evidence_count || 0}</p>
            <p className="text-xs text-gray-500 mt-1">Total Evidence Items</p>
          </div>
        </div>
      )}

      {/* Health & Collection Status */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <p className="text-xs text-gray-500">Collection</p>
          <p className="text-sm font-medium text-gray-900">
            {source.collection_frequency || 'On Demand'}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Health</p>
          <p className={`text-sm font-medium ${
            source.health_status === 'healthy' ? 'text-green-600' :
            source.health_status === 'unhealthy' ? 'text-red-600' :
            source.health_status === 'degraded' ? 'text-orange-600' :
            'text-yellow-600'
          }`}>
            {source.health_status || 'unknown'}
          </p>
        </div>
      </div>

      {/* Frameworks */}
      {source.regulatory_frameworks && source.regulatory_frameworks.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-gray-500 mb-2">Frameworks</p>
          <div className="flex flex-wrap gap-1">
            {source.regulatory_frameworks.slice(0, 3).map((framework, idx) => (
              <span
                key={idx}
                className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded"
              >
                {framework}
              </span>
            ))}
            {source.regulatory_frameworks.length > 3 && (
              <span className="px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded">
                +{source.regulatory_frameworks.length - 3} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* An archived source (e.g. Elastic SIEM, retired) no longer collects: say why, no Sync. */}
      {source.status === 'archived' ? (
        <p className="w-full rounded-lg bg-gray-50 border border-gray-200 px-4 py-2 text-xs text-gray-600" data-testid="evidence-source-archived">
          {source.error_message || 'This source is archived and no longer collects. The evidence it collected is kept.'}
        </p>
      ) : (
      <button
        onClick={handleSync}
        disabled={isSyncing || source.status === 'syncing'}
        className={`w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
          isSyncing || source.status === 'syncing'
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-blue-600 text-white hover:bg-blue-700'
        }`}
      >
        <RefreshCw className={`h-4 w-4 ${isSyncing || source.status === 'syncing' ? 'animate-spin' : ''}`} />
        {isSyncing || source.status === 'syncing'
          ? (source.source_type === 'ssl_labs' ? 'Scanning...' : source.source_type?.startsWith('siem') ? 'Collecting...' : 'Syncing...')
          : (source.source_type === 'ssl_labs' ? 'Scan Now →' : source.source_type?.startsWith('siem') ? 'Collect Logs →' : 'Sync Now')
        }
      </button>
      )}
    </div>
  );
};

export default EvidenceSourceCard;
